import type { JSX } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me, Category, Supplier, SpendLine, Contract, SavingsInitiative, KraljicPosition, ScorecardEntry, Stakeholder, ImportSuppliersResult, SupplierLookupResult, PortalInvite, PortalSubmission, ContractTemplate, TemplateAllocation, TemplateDisclaimer, TemplateRender, ContractCoFill, CategoryStrategy as CategoryStrategyRec, BusinessReview, TodayView, Member, Org, AuditEvent, ImportPreviewResponse, ImportCommitResponse } from '@procuai/contracts';
import { api, ApiError, getCredits } from '../api.js';
import {
  Start,
  SavingsPipeline, CategoryStrategy, MasterData, Suppliers, Spend, TailSpend, PaymentTerms,
  Maturity, MegaTrends, ProcurementStrategy, CategoryIntelligence, RegulatoryWatch,
  SourcingProjects, LeverLibrary, StakeholderMap, BusinessReviews,
  KraljicSegmentation, PerformanceScorecard, ContractRepository, ExpiryCalendar,
  SecurityOfSupply, SupplierNews, EsgScorecard, FinancialHealth,
  PolicyBuilder, ChartOfAuthority, ReportingDashboard, TargetBoard,
  ModuleStub, UnlockCard, navEntryFor, SETTINGS_SECTIONS,
  AcceptedTerms, type AcceptedTermsAdapter,
  ContractTemplates, type ContractTemplatesAdapter,
  type CategoryStrategyAdapter, type BusinessReviewsAdapter, type TailSpendAdapter, type PaymentTermsAdapter,
  type TodayAdapter,
  ImportWorkbook, type ImportAdapter,
  SettingsProfile, SettingsMembers, SettingsOrganisation, AuditLog,
  type MembersAdapter, type OrganisationAdapter, type AuditAdapter, type AuditFilters, type AuditEventView,
} from '@procuai/ui';
import { getLegalDocument, getPublicLegalDocument, getTermsAcceptances } from '../api.js';

/** Preview/demo build flag (no API, no login): keep the Categories tab on the fixture in preview. */
const PREVIEW = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_PREVIEW === '1';

/**
 * Resolves a module route to its content: the screens ported from approved prototypes
 * (Category Strategy — D-104, Master Data — D-100/D-101, Savings Pipeline), an unlock card for
 * gated modules (Master Spec §2/§4), or a "coming in build" stub otherwise.
 */
export function ModulePage({ path }: { path: string }): JSX.Element {
  const navigate = useNavigate();
  const router = useRouter();
  const go = (to: string) => void navigate({ to: to as never });
  // Close Settings by returning to the previous screen, leaving it as it was (Henrik, 16 Jun).
  const closeToPrevious = () => { if (window.history.length > 1) router.history.back(); else go('/'); };
  const me = useQueryClient().getQueryData<Me>(['me']);

  // Landing
  if (path === '/start') return PREVIEW ? <Start onNavigate={go} orgName={me?.org?.name} /> : <StartLive onNavigate={go} orgName={me?.org?.name} />;
  if (path === '/import') return PREVIEW ? <ModuleStub title="Start with your own data" /> : <ImportLive onNavigate={go} />;
  // Strategy
  if (path === '/maturity') return <Maturity onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/mega-trends') return <MegaTrends onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/procurement-strategy') return <ProcurementStrategy onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/category-strategy') return PREVIEW ? <CategoryStrategy onNavigate={go} /> : <CategoryStrategyLive onNavigate={go} />;
  if (path === '/category-intelligence') return <CategoryIntelligence onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/regulatory-watch') return <RegulatoryWatch onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  // Spend
  if (path === '/spend-overview') return PREVIEW ? <Spend onNavigate={go} /> : <SpendLive onNavigate={go} />;
  if (path === '/tail-spend') return PREVIEW ? <TailSpend onNavigate={go} /> : <TailSpendLive onNavigate={go} />;
  if (path === '/payment-terms') return PREVIEW ? <PaymentTerms onNavigate={go} /> : <PaymentTermsLive onNavigate={go} />;
  if (path === '/savings-pipeline') return PREVIEW ? <SavingsPipeline /> : <SavingsLive />;
  // Sourcing
  if (path === '/sourcing') return <SourcingProjects onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/lever-library') return <LeverLibrary onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  // Stakeholders
  if (path === '/stakeholder-map') return PREVIEW ? <StakeholderMap onNavigate={go} /> : <StakeholderLive onNavigate={go} />;
  if (path === '/business-reviews') return PREVIEW ? <BusinessReviews onNavigate={go} /> : <BusinessReviewsLive onNavigate={go} />;
  // Supplier Relationship
  if (path === '/kraljic') return PREVIEW ? <KraljicSegmentation onNavigate={go} /> : <KraljicLive onNavigate={go} />;
  if (path === '/scorecard') return PREVIEW ? <PerformanceScorecard onNavigate={go} /> : <ScorecardLive onNavigate={go} />;
  // Contract
  if (path === '/contract-repository') return PREVIEW ? <ContractRepository onNavigate={go} /> : <ContractsLive onNavigate={go} />;
  if (path === '/expiry-calendar') return PREVIEW ? <ExpiryCalendar onNavigate={go} /> : <ExpiryCalendarLive onNavigate={go} />;
  if (path === '/contract-templates') return PREVIEW ? <ModuleStub title="Contract Templates" /> : <ContractTemplatesLive onNavigate={go} />;
  // Risk
  if (path === '/security-of-supply') return <SecurityOfSupply onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/supplier-news') return <SupplierNews onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/esg-scorecard') return <EsgScorecard onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/financial-health') return <FinancialHealth onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  // Governance
  if (path === '/policy-builder') return <PolicyBuilder onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/chart-of-authority') return <ChartOfAuthority onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  // Impact
  if (path === '/reporting') return <ReportingDashboard onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  if (path === '/target-board') return <TargetBoard onNavigate={go} liveFirst={me?.demoDismissed ?? false} />;
  // Settings + cross-cutting
  if (path === '/master-data') return PREVIEW ? <MasterData onNavigate={go} /> : <MasterDataLive onNavigate={go} />;
  if (path === '/suppliers') return PREVIEW ? <Suppliers onNavigate={go} /> : <SuppliersLive onNavigate={go} />;
  if (path === '/settings') return <SettingsIndex onNavigate={go} onClose={closeToPrevious} />;
  if (path === '/settings/terms') return <AcceptedTermsLive userEmail={me?.user.email ?? ''} onBack={() => go('/settings')} />;
  if (path === '/settings/profile') return <SettingsProfile a={{ email: me?.user.email ?? '', role: me?.role ?? null, orgName: me?.org?.name ?? null, hasPassword: me?.user.hasPassword ?? false, totpEnabled: me?.user.totpEnabled ?? false }} onNavigate={go} onBack={() => go('/settings')} />;
  if (path === '/settings/members') return <MembersLive meEmail={me?.user.email ?? ''} canWrite={me?.role === 'org_admin' || me?.role === 'super_admin'} onBack={() => go('/settings')} />;
  if (path === '/settings/organisation') return <OrganisationLive canWrite={me?.role === 'org_admin' || me?.role === 'super_admin'} onNavigate={go} onBack={() => go('/settings')} />;
  if (path === '/settings/audit') return <AuditLive canRead={me?.role === 'org_admin' || me?.role === 'super_admin'} onBack={() => go('/settings')} />;

  const entry = navEntryFor(path);
  const title = entry?.label ?? 'Module';
  if (entry?.locked) return <UnlockCard title={title} />;
  return <ModuleStub title={title} />;
}

/**
 * Minimal Settings landing: lists the Settings sections (D-103). Master Data is the first built
 * section; the rest follow as they are designed and ported. Built sections link in; others are
 * tagged as coming so the placement is visible without claiming a screen that does not exist yet.
 * A Close control returns to the previous screen so Settings behaves like a window over the app.
 */
function SettingsIndex({ onNavigate, onClose }: { onNavigate: (path: string) => void; onClose: () => void }): JSX.Element {
  const built = new Set(SETTINGS_SECTIONS.map((s) => s.path));
  const creditsQ = useQuery({ queryKey: ['credits'], queryFn: getCredits });
  const c = creditsQ.data;
  const barColor = c?.over ? 'var(--red)' : c?.warn ? 'var(--amber)' : 'var(--pine)';
  const sections: { label: string; path?: string; note: string }[] = [
    { label: 'Master Data', path: '/master-data', note: 'Suppliers, categories, organisation + reference data and data quality' },
    { label: 'Your accepted terms', path: '/settings/terms', note: 'Every terms version you accepted, word for word, with download' },
    { label: 'Profile & Security', path: '/settings/profile', note: 'Your account, sign-in method, role, and where two-factor will live' },
    { label: 'Members', path: '/settings/members', note: 'Invite people and manage roles for your organisation' },
    { label: 'Organisation', path: '/settings/organisation', note: 'Legal name and the legal profile your contract templates fill from' },
    { label: 'Folders', note: 'Your connected cloud folder and folder integrations' },
    { label: 'Audit Log', path: '/settings/audit', note: 'The immutable, exportable record an auditor pulls' },
  ];
  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--pine)', marginBottom: 6 }}>Settings</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, letterSpacing: '-.02em', marginBottom: 8 }}>Settings</h1>
          <div style={{ color: 'var(--ink-soft)', fontSize: 12.5 }}>Your organisation, people and the governed master data every module reads from.</div>
        </div>
        <button
          onClick={onClose}
          title="Close settings and return to your previous screen"
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--radius-btn)', padding: '8px 14px', fontWeight: 600, fontSize: 13, color: 'var(--ink)', cursor: 'pointer' }}
        >
          ✕ Close settings
        </button>
      </div>
      <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)', padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>AI usage this month</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--ink-soft)' }}>{creditsQ.isLoading ? 'loading…' : c ? `${c.used.toLocaleString('en-GB')} / ${c.allowance.toLocaleString('en-GB')} credits` : 'unavailable'}</span>
          {c && <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: barColor }}>{c.remaining.toLocaleString('en-GB')} left</span>}
        </div>
        <div style={{ height: 8, borderRadius: 5, background: 'var(--paper)', border: '1px solid var(--line)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${c ? Math.min(100, Math.round(c.pct * 100)) : 0}%`, background: barColor }} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.5 }}>
          {c?.over
            ? 'You have used your monthly credits. Running tasks still finish; new ones may ask you to top up.'
            : c?.warn
              ? 'You have used over 80% of this month’s credits.'
              : 'Credits cover the Orchestrator’s AI work. Your monthly allowance resets at the start of each month.'}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 12 }}>
        {sections.map((s) => {
          const live = s.path && built.has(s.path);
          return (
            <button
              key={s.label}
              onClick={() => (live ? onNavigate(s.path!) : undefined)}
              style={{
                textAlign: 'left', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--radius-card)',
                padding: '14px 15px', boxShadow: 'var(--shadow-card)', cursor: live ? 'pointer' : 'default', opacity: live ? 1 : 0.7,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{s.label}</span>
                {!live && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '.04em', textTransform: 'uppercase', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 999, padding: '1px 7px', color: 'var(--ink-soft)' }}>soon</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{s.note}</div>
              {live && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--pine-deep)', marginTop: 8 }}>Open →</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Master Data, live (Phase 1, D-100/D-101/D-109): the Categories tab reads and writes the real
 * Category Register through the audited API. Read = any member; create/delete = org_admin (gated
 * server-side too; the UI hides the controls for non-admins). The other Master Data tabs stay on
 * their demo fixtures until their domains are persisted in later waves.
 */
function MasterDataLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const canWrite = me?.role === 'org_admin' || me?.role === 'super_admin';
  const { data, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<Category[]>('/master-data/categories'),
  });
  const createM = useMutation({
    mutationFn: (name: string) => api<Category>('/master-data/categories', { method: 'POST', body: JSON.stringify({ name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });
  const removeM = useMutation({
    mutationFn: (id: string) => api<void>(`/master-data/categories/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const supCreateM = useMutation({
    mutationFn: (name: string) => api<Supplier>('/master-data/suppliers', { method: 'POST', body: JSON.stringify({ name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
  const supRemoveM = useMutation({
    mutationFn: (id: string) => api<void>(`/master-data/suppliers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
  const supUpdateM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api<Supplier>(`/master-data/suppliers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
  const supImportM = useMutation({
    mutationFn: (rows: Record<string, unknown>[]) => api<ImportSuppliersResult>('/master-data/suppliers/import', { method: 'POST', body: JSON.stringify({ rows }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
  // Supplier portal (BP-02 §2.4): invite a contact by email, then accept/reject what they send back.
  // The panel loads a supplier's invites + submissions on open; accept writes to the master record.
  const portal = canWrite ? {
    load: async (supplierId: string) => {
      const [invites, submissions] = await Promise.all([
        api<PortalInvite[]>(`/master-data/suppliers/${supplierId}/portal-invites`),
        api<PortalSubmission[]>(`/master-data/suppliers/${supplierId}/portal-submissions`),
      ]);
      return {
        invites: invites.map((iv) => ({ id: iv.id, contactId: iv.contactId, contactName: iv.contactName, contactEmail: iv.contactEmail, status: iv.status, expiresAt: iv.expiresAt, submittedAt: iv.submittedAt, lastSentAt: iv.lastSentAt, pendingReviewCount: iv.pendingReviewCount, erasureRequested: iv.erasureRequested, consentVersion: iv.consentVersion, consentAcceptedAt: iv.consentAcceptedAt, hasContract: iv.hasContract })),
        submissions: submissions.map((s) => ({ id: s.id, fieldKey: s.fieldKey, fieldLabel: s.fieldLabel, value: s.value, currentValue: s.currentValue, reviewStatus: s.reviewStatus })),
      };
    },
    invite: async (supplierId: string, input: { contactName: string; contactEmail: string; contactRole?: string | undefined }) => {
      await api<PortalInvite>(`/master-data/suppliers/${supplierId}/portal-invites`, { method: 'POST', body: JSON.stringify(input) });
      await qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
    resend: async (inviteId: string) => { await api<PortalInvite>(`/portal-invites/${inviteId}/resend`, { method: 'POST' }); },
    review: async (submissionId: string, status: 'accepted' | 'rejected') => {
      await api<PortalSubmission>(`/portal-submissions/${submissionId}`, { method: 'PATCH', body: JSON.stringify({ reviewStatus: status }) });
      await qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
    completeErasure: async (inviteId: string) => {
      await api<PortalInvite>(`/portal-invites/${inviteId}/complete-erasure`, { method: 'POST' });
      await qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
    // D-158 contract co-fill: list published templates, send one for co-fill, load + review it.
    coFillTemplates: async () => (await api<ContractTemplate[]>('/contract-templates')).filter((t) => t.status === 'published').map((t) => ({ id: t.id, familyId: t.familyId, version: t.version, name: t.name })),
    sendCoFill: async (supplierId: string, templateId: string, contact: { contactName: string; contactEmail: string }) => {
      const list = await api<ContractTemplate[]>('/contract-templates');
      const t = list.find((x) => x.id === templateId);
      if (!t) throw new Error('Template not found');
      const rend = await api<TemplateRender>(`/contract-templates/${templateId}/render?supplierId=${supplierId}`);
      await api<PortalInvite>(`/master-data/suppliers/${supplierId}/portal-invites`, { method: 'POST', body: JSON.stringify({ contactName: contact.contactName, contactEmail: contact.contactEmail, contract: { familyId: t.familyId, version: t.version, supplierTokens: rend.unfilled } }) });
      await qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
    loadCoFill: async (inviteId: string) => {
      const c = await api<ContractCoFill>(`/portal-invites/${inviteId}/cofill`);
      return { contractName: c.contractName, contractVersion: c.contractVersion, status: c.status, bodyMd: c.bodyMd, tokens: c.tokens.map((t) => ({ name: t.name, label: t.label, value: t.value, reviewStatus: t.reviewStatus })) };
    },
    reviewToken: async (inviteId: string, token: string, status: 'accepted' | 'rejected') => {
      const c = await api<ContractCoFill>(`/portal-invites/${inviteId}/tokens/review`, { method: 'POST', body: JSON.stringify({ token, reviewStatus: status }) });
      return { contractName: c.contractName, contractVersion: c.contractVersion, status: c.status, bodyMd: c.bodyMd, tokens: c.tokens.map((t) => ({ name: t.name, label: t.label, value: t.value, reviewStatus: t.reviewStatus })) };
    },
    // BP-22: the exact notice text this supplier accepted, from the versioned store, with the
    // {Customer}/{Supplier} tokens filled the same way the portal filled them.
    loadNotice: async (version: string, supplierId: string) => {
      const doc = await getLegalDocument('supplier_portal_notice', version);
      const orgName = me?.org?.name ?? 'your organisation';
      const supplierName = (supQ.data ?? []).find((s) => s.id === supplierId)?.name ?? 'the supplier';
      return { title: doc.title, bodyMd: doc.bodyMd.replaceAll('{Customer}', orgName).replaceAll('{Supplier}', supplierName) };
    },
    // Bank info (D-141) is edited on the supplier Edit form (the PATCH below) and validated by the
    // supplier's finance colleague via their own link; there are no buyer-side bank endpoints here.
  } : undefined;
  return (
    <MasterData
      onNavigate={onNavigate}
      categories={{
        rows: (data ?? []).map((c) => ({ id: c.id, name: c.name, unspscCode: c.unspscCode, ownerName: null, isDemo: c.isDemo })),
        loading: isLoading,
        error: error ? (error instanceof ApiError ? error.message : 'Could not load categories') : null,
        canWrite,
        create: async (name: string) => { await createM.mutateAsync(name); },
        remove: async (id: string) => { await removeM.mutateAsync(id); },
      }}
      suppliers={{
        rows: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name, legalName: s.legalName, country: s.country, vatNumber: s.vatNumber, lei: s.lei, duns: s.duns, address: s.address, contactName: s.contactName, contactEmail: s.contactEmail, contactPhone: s.contactPhone, iban: s.iban, bic: s.bic, bankName: s.bankName, accountHolder: s.accountHolder, paymentTerms: s.paymentTerms, bankValidationStatus: s.bankValidationStatus, bankFinanceEmail: s.bankFinanceEmail, status: s.status, isDemo: s.isDemo })),
        loading: supQ.isLoading,
        error: supQ.error ? (supQ.error instanceof ApiError ? supQ.error.message : 'Could not load suppliers') : null,
        canWrite,
        create: async (name: string) => { await supCreateM.mutateAsync(name); },
        update: async (id: string, patch) => { await supUpdateM.mutateAsync({ id, patch: patch as Record<string, unknown> }); },
        remove: async (id: string) => { await supRemoveM.mutateAsync(id); },
        importRows: canWrite ? (rows: Record<string, unknown>[]) => supImportM.mutateAsync(rows) : undefined,
        lookup: async (q) => {
          const params = new URLSearchParams();
          if (q.lei) params.set('lei', q.lei);
          if (q.vatNumber) params.set('vatNumber', q.vatNumber);
          if (q.country) params.set('country', q.country);
          return api<SupplierLookupResult>(`/master-data/suppliers/lookup?${params.toString()}`);
        },
        portal,
      }}
    />
  );
}

/**
 * Suppliers cockpit, live (D-061/D-112; U5 in D-151 Batch 3): the cockpit keeps its rich demo dataset,
 * your real suppliers appear in the labelled live registry, and each row now carries its allocated
 * governing paper (from Contract Templates) with a two-way route: Paper opens the template module
 * with this supplier preselected; a supplier allocation over there routes back here.
 */
function SuppliersLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const me = useQueryClient().getQueryData<Me>(['me']);
  const { data } = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const allocQ = useQuery({ queryKey: ['template-allocations'], queryFn: () => api<TemplateAllocation[]>('/contract-templates/allocations') });
  const tplQ = useQuery({ queryKey: ['contract-templates'], queryFn: () => api<ContractTemplate[]>('/contract-templates') });
  const paperOf = (supplierId: string): string[] =>
    (allocQ.data ?? [])
      .filter((al) => al.targetType === 'supplier' && al.targetId === supplierId)
      .map((al) => `${(tplQ.data ?? []).find((t) => t.familyId === al.familyId && t.version === al.templateVersion)?.name ?? 'template'} v${al.templateVersion}`);
  const live = (data ?? []).map((s) => ({ id: s.id, name: s.name, country: s.country, status: s.status, vatNumber: s.vatNumber, lei: s.lei, duns: s.duns, isDemo: s.isDemo, paper: paperOf(s.id) }));
  // D-155a: live-first once the org wiped demo or has real rows in this module.
  const liveFirst = (me?.demoDismissed ?? false) || live.some((s) => !s.isDemo);
  return <Suppliers onNavigate={onNavigate} liveSuppliers={live} liveFirst={liveFirst} />;
}

/**
 * Start, live (D-151 Batch 3): the landing gains the "Today" strip, a read-only aggregation of what
 * needs the category manager now, each row routing into the module that resolves it.
 */
function StartLive({ onNavigate, orgName }: { onNavigate: (path: string) => void; orgName?: string | undefined }): JSX.Element {
  const todayQ = useQuery({ queryKey: ['start-today'], queryFn: () => api<TodayView>('/start/today') });
  const adapter: TodayAdapter = {
    data: todayQ.data ?? null,
    loading: todayQ.isLoading,
    error: todayQ.error ? (todayQ.error instanceof ApiError ? todayQ.error.message : 'Could not load the Today strip') : null,
  };
  return <Start onNavigate={onNavigate} orgName={orgName} todayStrip={adapter} />;
}

/**
 * Start with your own data, live (D-155b, Batch 5): the onboarding workbook flow. Template download
 * through the cookie-authenticated fetch (preserving the server's filename); preview + commit post
 * the workbook (or CSVs) as JSON; the wipe prompt reuses the audited /demo/wipe. Org Admin gated
 * server-side; the UI mirrors it.
 */
function ImportLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const canWrite = me?.role === 'org_admin' || me?.role === 'super_admin';
  const adapter: ImportAdapter = {
    canWrite,
    downloadTemplate: async () => {
      const res = await fetch('/api/v1/import/template', { credentials: 'include' });
      if (!res.ok) throw new Error('Could not download the workbook');
      const blob = await res.blob();
      const name = res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'procuai-onboarding-workbook.xlsx';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      URL.revokeObjectURL(url);
    },
    preview: (payload) => api<ImportPreviewResponse>('/import/preview', { method: 'POST', body: JSON.stringify(payload) }),
    commit: async (payload) => {
      const out = await api<ImportCommitResponse>('/import/commit', { method: 'POST', body: JSON.stringify(payload) });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['categories'] }),
        qc.invalidateQueries({ queryKey: ['suppliers'] }),
        qc.invalidateQueries({ queryKey: ['contracts'] }),
        qc.invalidateQueries({ queryKey: ['spend', 'lines'] }),
        qc.invalidateQueries({ queryKey: ['start-today'] }),
      ]);
      return out;
    },
    wipeDemo: async () => {
      await api<{ wiped: number }>('/demo/wipe', { method: 'POST' });
      await qc.invalidateQueries();
    },
  };
  return <ImportWorkbook adapter={adapter} onNavigate={onNavigate} />;
}

/**
 * Spend Overview, live (BP-08/D-047/D-113): the demo cube/findings stay, and your real spend lines
 * (referencing the live suppliers + categories) appear in a labelled panel at the top, with a running
 * total and an add form. The cube/KPIs/findings go live as the AP-upload + AI classification land.
 */
function SpendLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const canWrite = me?.role === 'org_admin' || me?.role === 'super_admin';
  const linesQ = useQuery({ queryKey: ['spend', 'lines'], queryFn: () => api<SpendLine[]>('/spend/lines') });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/master-data/categories') });
  const createM = useMutation({
    mutationFn: (input: Record<string, unknown>) => api<SpendLine>('/spend/lines', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spend', 'lines'] }),
  });
  const removeM = useMutation({
    mutationFn: (id: string) => api<void>(`/spend/lines/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spend', 'lines'] }),
  });
  const liveFirst = (me?.demoDismissed ?? false) || (linesQ.data ?? []).some((l) => !l.isDemo);
  return (
    <Spend
      onNavigate={onNavigate}
      liveFirst={liveFirst}
      spend={{
        rows: (linesQ.data ?? []).map((l) => ({ id: l.id, supplierId: l.supplierId, categoryId: l.categoryId, description: l.description, amount: l.amount, currency: l.currency, spendDate: l.spendDate, isDemo: l.isDemo })),
        suppliers: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name })),
        categories: (catQ.data ?? []).map((c) => ({ id: c.id, name: c.name })),
        loading: linesQ.isLoading,
        error: linesQ.error ? (linesQ.error instanceof ApiError ? linesQ.error.message : 'Could not load spend lines') : null,
        canWrite,
        create: async (input) => { await createM.mutateAsync(input as unknown as Record<string, unknown>); },
        remove: async (id) => { await removeM.mutateAsync(id); },
      }}
    />
  );
}

/**
 * Contract Repository, live (BP-05/D-043/D-114): the demo register/coverage analytics stay, and your
 * real contracts (referencing the live suppliers + categories) appear in a labelled panel at the top,
 * with an add form. The register/coverage/renewal analytics go live as document upload + AI extraction land.
 */
function ContractsLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const canWrite = me?.role === 'org_admin' || me?.role === 'super_admin';
  const conQ = useQuery({ queryKey: ['contracts'], queryFn: () => api<Contract[]>('/contracts') });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/master-data/categories') });
  const createM = useMutation({
    mutationFn: (input: Record<string, unknown>) => api<Contract>('/contracts', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });
  const removeM = useMutation({
    mutationFn: (id: string) => api<void>(`/contracts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });
  const updateM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api<Contract>(`/contracts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });
  const liveFirst = (me?.demoDismissed ?? false) || (conQ.data ?? []).some((c) => !c.isDemo);
  return (
    <ContractRepository
      onNavigate={onNavigate}
      liveFirst={liveFirst}
      contracts={{
        rows: (conQ.data ?? []).map((c) => ({ id: c.id, supplierId: c.supplierId, categoryId: c.categoryId, title: c.title, status: c.status, startDate: c.startDate, endDate: c.endDate, noticePeriodDays: c.noticePeriodDays, value: c.value, currency: c.currency, isDemo: c.isDemo })),
        suppliers: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name })),
        categories: (catQ.data ?? []).map((c) => ({ id: c.id, name: c.name })),
        loading: conQ.isLoading,
        error: conQ.error ? (conQ.error instanceof ApiError ? conQ.error.message : 'Could not load contracts') : null,
        canWrite,
        create: async (input) => { await createM.mutateAsync(input as unknown as Record<string, unknown>); },
        update: async (id, patch) => { await updateM.mutateAsync({ id, patch: patch as unknown as Record<string, unknown> }); },
        remove: async (id) => { await removeM.mutateAsync(id); },
      }}
    />
  );
}

/**
 * Expiry Calendar, live (BP-05/D-038/D-114; D-151 Batch 2): the labelled panel lists your real
 * contracts with an end date, and the renewal timeline + pipeline below run on the UNION of your
 * real contracts and the demo book (D-150 recipe): real date-derived cliffs from the stored notice
 * period, demo rows amber-tagged, reduced live detail until BP-05 extraction. Read-only; no new endpoint.
 */
function ExpiryCalendarLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const me = useQueryClient().getQueryData<Me>(['me']);
  const conQ = useQuery({ queryKey: ['contracts'], queryFn: () => api<Contract[]>('/contracts') });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/master-data/categories') });
  const supName = (id: string | null): string | null => (id ? supQ.data?.find((s) => s.id === id)?.name ?? null : null);
  const catName = (id: string | null): string | null => (id ? catQ.data?.find((c) => c.id === id)?.name ?? null : null);
  return (
    <ExpiryCalendar
      onNavigate={onNavigate}
      liveFirst={(me?.demoDismissed ?? false) || (conQ.data ?? []).some((c) => !c.isDemo)}
      expiry={{
        rows: (conQ.data ?? []).map((c) => ({ id: c.id, title: c.title, supplierName: supName(c.supplierId), status: c.status, endDate: c.endDate, noticePeriodDays: c.noticePeriodDays, value: c.value, currency: c.currency, categoryName: catName(c.categoryId), isDemo: c.isDemo })),
        loading: conQ.isLoading,
        error: conQ.error ? (conQ.error instanceof ApiError ? conQ.error.message : 'Could not load contracts') : null,
      }}
    />
  );
}

/**
 * Category Strategy, live (D-151 Batch 2): the persisted strategy-on-a-page per category in a labelled
 * panel (member-write, audited; the Orchestrator reads these), with the approved demo page below.
 */
function CategoryStrategyLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const stratQ = useQuery({ queryKey: ['category-strategies'], queryFn: () => api<CategoryStrategyRec[]>('/category-strategies') });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/master-data/categories') });
  const refresh = (): Promise<void> => qc.invalidateQueries({ queryKey: ['category-strategies'] });
  const adapter: CategoryStrategyAdapter = {
    rows: (stratQ.data ?? []).map((s) => ({ id: s.id, categoryId: s.categoryId, position: s.position, statement: s.statement, objectivesMd: s.objectivesMd, leversMd: s.leversMd, roadmapMd: s.roadmapMd, status: s.status, isDemo: s.isDemo, updatedAt: s.updatedAt })),
    categories: (catQ.data ?? []).map((c) => ({ id: c.id, name: c.name, isDemo: c.isDemo })),
    loading: stratQ.isLoading,
    error: stratQ.error ? (stratQ.error instanceof ApiError ? stratQ.error.message : 'Could not load category strategies') : null,
    canWrite: true, // D-151 Batch 2: member-write; the API still authenticates and audits every write
    create: async (input) => { await api<CategoryStrategyRec>('/category-strategies', { method: 'POST', body: JSON.stringify(input) }); await refresh(); },
    update: async (id, patch) => { await api<CategoryStrategyRec>(`/category-strategies/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }); await refresh(); },
    remove: async (id) => { await api<void>(`/category-strategies/${id}`, { method: 'DELETE' }); await refresh(); },
  };
  const liveFirst = (me?.demoDismissed ?? false) || (stratQ.data ?? []).some((s) => !s.isDemo);
  return <CategoryStrategy onNavigate={onNavigate} strategies={adapter} liveFirst={liveFirst} />;
}

/**
 * Business Reviews, live (D-151 Batch 2): the supplier review cadence in a labelled panel — next date,
 * an agenda prefilled from live scorecard + contract + savings data, minutes and an action register.
 * Member-write, audited; the Orchestrator reads these. The approved demo pack stays below.
 */
function BusinessReviewsLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const revQ = useQuery({ queryKey: ['business-reviews'], queryFn: () => api<BusinessReview[]>('/business-reviews') });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const scoreQ = useQuery({ queryKey: ['scorecards'], queryFn: () => api<ScorecardEntry[]>('/scorecards/entries') });
  const conQ = useQuery({ queryKey: ['contracts'], queryFn: () => api<Contract[]>('/contracts') });
  const savQ = useQuery({ queryKey: ['savings'], queryFn: () => api<SavingsInitiative[]>('/savings/initiatives') });
  const refresh = (): Promise<void> => qc.invalidateQueries({ queryKey: ['business-reviews'] });
  // The data-prefilled agenda (D-151 Batch 2): every figure comes from a live record; sections with
  // no data say so honestly instead of inventing numbers.
  const prefillAgenda = (supplierId: string): string => {
    const name = supQ.data?.find((s) => s.id === supplierId)?.name ?? 'the supplier';
    const kpiLabel: Record<string, string> = { otif: 'OTIF', quality: 'Quality', price: 'Price', responsiveness: 'Responsiveness', relationship: 'Relationship' };
    const entries = (scoreQ.data ?? []).filter((e) => e.supplierId === supplierId);
    const latestByKpi = new Map<string, ScorecardEntry>();
    for (const e of entries) {
      const cur = latestByKpi.get(e.kpi);
      if (!cur || e.period > cur.period) latestByKpi.set(e.kpi, e);
    }
    const scoreLines = [...latestByKpi.values()].map((e) => `- ${kpiLabel[e.kpi] ?? e.kpi}: ${e.score} (${e.period})`);
    const cons = (conQ.data ?? []).filter((c) => c.supplierId === supplierId);
    const conLines = cons.map((c) => `- ${c.title}: ${c.status}${c.endDate !== null ? `, ends ${c.endDate}` : ''}${c.value !== null ? `, ${c.value} ${c.currency}` : ''}`);
    const savs = (savQ.data ?? []).filter((s) => s.supplierId === supplierId);
    const savLines = savs.map((s) => `- ${s.title}: ${s.ilStage}${s.value !== null ? `, ${s.value} ${s.currency}` : ''}`);
    return [
      `## Agenda · ${name}`,
      '',
      '### 1. Performance vs scorecard',
      ...(scoreLines.length > 0 ? scoreLines : ['- No scorecard entries recorded yet (record them on the Performance Scorecard).']),
      '',
      '### 2. Open actions from the last review',
      '- Walk the action register below.',
      '',
      '### 3. Contracts and commercial topics',
      ...(conLines.length > 0 ? conLines : ['- No contracts recorded for this supplier yet.']),
      '',
      '### 4. Savings and improvement initiatives',
      ...(savLines.length > 0 ? savLines : ['- No savings initiatives recorded for this supplier yet.']),
      '',
      '### 5. Outlook, capacity and next steps',
      '- Demand outlook, risks, and the date of the next review.',
    ].join('\n');
  };
  const adapter: BusinessReviewsAdapter = {
    rows: (revQ.data ?? []).map((r) => ({ id: r.id, supplierId: r.supplierId, title: r.title, cadence: r.cadence, nextDate: r.nextDate, agendaMd: r.agendaMd, minutesMd: r.minutesMd, actions: r.actions, isDemo: r.isDemo })),
    suppliers: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name, isDemo: s.isDemo })),
    loading: revQ.isLoading,
    error: revQ.error ? (revQ.error instanceof ApiError ? revQ.error.message : 'Could not load business reviews') : null,
    canWrite: true, // D-151 Batch 2: member-write; the API still authenticates and audits every write
    create: async (input) => { await api<BusinessReview>('/business-reviews', { method: 'POST', body: JSON.stringify(input) }); await refresh(); },
    update: async (id, patch) => { await api<BusinessReview>(`/business-reviews/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }); await refresh(); },
    remove: async (id) => { await api<void>(`/business-reviews/${id}`, { method: 'DELETE' }); await refresh(); },
    prefillAgenda,
  };
  const liveFirst = (me?.demoDismissed ?? false) || (revQ.data ?? []).some((r) => !r.isDemo);
  return <BusinessReviews onNavigate={onNavigate} reviews={adapter} liveFirst={liveFirst} />;
}

/**
 * Tail Spend, live (D-151 Batch 2): the tail computed client-side from the real spend cube — suppliers
 * whose 12-month total sits below the adjustable cut line. Read-only aggregation; no new endpoint.
 */
function TailSpendLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const linesQ = useQuery({ queryKey: ['spend', 'lines'], queryFn: () => api<SpendLine[]>('/spend/lines') });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const adapter: TailSpendAdapter = {
    lines: (linesQ.data ?? []).map((l) => ({ supplierId: l.supplierId, amount: l.amount, spendDate: l.spendDate, isDemo: l.isDemo })),
    suppliers: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name, isDemo: s.isDemo })),
    loading: linesQ.isLoading,
    error: linesQ.error ? (linesQ.error instanceof ApiError ? linesQ.error.message : 'Could not load spend lines') : null,
  };
  return <TailSpend onNavigate={onNavigate} tail={adapter} />;
}

/**
 * Payment Terms, live (D-151 Batch 2): the buyer-recorded terms from the supplier master (D-141),
 * weighted by real 12-month spend. Read-only aggregation; no new endpoint.
 */
function PaymentTermsLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const linesQ = useQuery({ queryKey: ['spend', 'lines'], queryFn: () => api<SpendLine[]>('/spend/lines') });
  const adapter: PaymentTermsAdapter = {
    suppliers: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name, paymentTerms: s.paymentTerms, isDemo: s.isDemo })),
    lines: (linesQ.data ?? []).map((l) => ({ supplierId: l.supplierId, amount: l.amount, spendDate: l.spendDate })),
    loading: supQ.isLoading,
    error: supQ.error ? (supQ.error instanceof ApiError ? supQ.error.message : 'Could not load suppliers') : null,
  };
  return <PaymentTerms onNavigate={onNavigate} terms={adapter} />;
}

/**
 * Kraljic Segmentation, live (BP-10, first depth slice): the demo matrix stays, and a labelled panel
 * lists your real supplier positions with an add form (supplier picker + risk/impact/segment). The
 * Orchestrator reads these positions (groundable). The interactive drag matrix goes live later.
 */
function KraljicLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const canWrite = me?.role === 'org_admin' || me?.role === 'super_admin';
  const posQ = useQuery({ queryKey: ['kraljic'], queryFn: () => api<KraljicPosition[]>('/kraljic/positions') });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/master-data/categories') });
  const createM = useMutation({
    mutationFn: (input: Record<string, unknown>) => api<KraljicPosition>('/kraljic/positions', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kraljic'] }),
  });
  const removeM = useMutation({
    mutationFn: (id: string) => api<void>(`/kraljic/positions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kraljic'] }),
  });
  const liveFirst = (me?.demoDismissed ?? false) || (posQ.data ?? []).some((p) => !p.isDemo);
  return (
    <KraljicSegmentation
      onNavigate={onNavigate}
      liveFirst={liveFirst}
      kraljic={{
        rows: (posQ.data ?? []).map((p) => ({ id: p.id, supplierId: p.supplierId, categoryId: p.categoryId, segment: p.segment, supplyRisk: p.supplyRisk, profitImpact: p.profitImpact, rationale: p.rationale, isDemo: p.isDemo })),
        suppliers: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name })),
        categories: (catQ.data ?? []).map((c) => ({ id: c.id, name: c.name })),
        loading: posQ.isLoading,
        error: posQ.error ? (posQ.error instanceof ApiError ? posQ.error.message : 'Could not load Kraljic positions') : null,
        canWrite,
        create: async (input) => { await createM.mutateAsync(input as unknown as Record<string, unknown>); },
        remove: async (id) => { await removeM.mutateAsync(id); },
      }}
    />
  );
}

/**
 * Contract Templates, live (BP-24, D-148/D-151): seeds + org versions, allocations, the disclaimer
 * gate (server-enforced; this wrapper only mirrors it) and the token-filled render. Members draft +
 * allocate; org_admin publishes/archives. All mutations audited inside templateService.
 */
function ContractTemplatesLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const canPublish = me?.role === 'org_admin' || me?.role === 'super_admin';
  const templatesQ = useQuery({ queryKey: ['contract-templates'], queryFn: () => api<ContractTemplate[]>('/contract-templates') });
  const allocationsQ = useQuery({ queryKey: ['template-allocations'], queryFn: () => api<TemplateAllocation[]>('/contract-templates/allocations') });
  const suppliersQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const disclaimerQ = useQuery({ queryKey: ['template-disclaimer'], queryFn: () => api<TemplateDisclaimer>('/contract-templates/disclaimer') });
  const refreshTemplates = (): void => { void qc.invalidateQueries({ queryKey: ['contract-templates'] }); };
  const refreshAllocations = (): void => { void qc.invalidateQueries({ queryKey: ['template-allocations'] }); };
  const adapter: ContractTemplatesAdapter = {
    templates: templatesQ.data ?? [],
    allocations: allocationsQ.data ?? [],
    suppliers: (suppliersQ.data ?? []).map((s) => ({ id: s.id, name: s.name, isDemo: s.isDemo })),
    disclaimer: disclaimerQ.data ?? null,
    loading: templatesQ.isLoading || allocationsQ.isLoading,
    error: templatesQ.error ? (templatesQ.error instanceof ApiError ? templatesQ.error.message : 'Could not load templates') : null,
    canPublish,
    acceptDisclaimer: async () => { await api('/contract-templates/disclaimer/accept', { method: 'POST', body: JSON.stringify({}) }); void qc.invalidateQueries({ queryKey: ['template-disclaimer'] }); },
    createDraft: async (sourceId, name) => { await api('/contract-templates/drafts', { method: 'POST', body: JSON.stringify(name ? { sourceId, name } : { sourceId }) }); refreshTemplates(); },
    updateDraft: async (id, patch) => { await api(`/contract-templates/drafts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }); refreshTemplates(); },
    deleteDraft: async (id) => { await api(`/contract-templates/drafts/${id}`, { method: 'DELETE' }); refreshTemplates(); },
    publish: async (id) => { await api(`/contract-templates/${id}/publish`, { method: 'POST', body: JSON.stringify({}) }); refreshTemplates(); },
    archive: async (id) => { await api(`/contract-templates/${id}/archive`, { method: 'POST', body: JSON.stringify({}) }); refreshTemplates(); },
    allocate: async (input) => { await api('/contract-templates/allocations', { method: 'POST', body: JSON.stringify(input) }); refreshAllocations(); },
    removeAllocation: async (id) => { await api(`/contract-templates/allocations/${id}`, { method: 'DELETE' }); refreshAllocations(); },
    render: async (id, supplierId) => api<TemplateRender>(`/contract-templates/${id}/render${supplierId ? `?supplierId=${supplierId}` : ''}`),
  };
  return <ContractTemplates adapter={adapter} onNavigate={onNavigate} />;
}

/**
 * Performance Scorecard, live (BP-23, D-146): the rich demo scorecard stays, and real KPI entries
 * appear in a labelled panel with the 30-second quick-entry form. Any member can record a score
 * (audited); the Orchestrator reads these (groundable, non-draftable — a create needs a supplier UUID).
 */
function ScorecardLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const entriesQ = useQuery({ queryKey: ['scorecards'], queryFn: () => api<ScorecardEntry[]>('/scorecards/entries') });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const createM = useMutation({
    mutationFn: (input: Record<string, unknown>) => api<ScorecardEntry>('/scorecards/entries', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scorecards'] }),
  });
  const removeM = useMutation({
    mutationFn: (id: string) => api<void>(`/scorecards/entries/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scorecards'] }),
  });
  const liveFirst = (me?.demoDismissed ?? false) || (entriesQ.data ?? []).some((e) => !e.isDemo);
  return (
    <PerformanceScorecard
      onNavigate={onNavigate}
      liveFirst={liveFirst}
      scorecard={{
        rows: (entriesQ.data ?? []).map((e) => ({ id: e.id, supplierId: e.supplierId, period: e.period, kpi: e.kpi, score: e.score, comment: e.comment, isDemo: e.isDemo })),
        suppliers: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name })),
        loading: entriesQ.isLoading,
        error: entriesQ.error ? (entriesQ.error instanceof ApiError ? entriesQ.error.message : 'Could not load scorecard entries') : null,
        canWrite: true, // D-146: member-write; the API still authenticates and audits every write
        create: async (input) => { await createM.mutateAsync(input as unknown as Record<string, unknown>); },
        remove: async (id) => { await removeM.mutateAsync(id); },
      }}
    />
  );
}

/**
 * Stakeholder Map, live (BP-12 depth slice): the approved demo map/board stay, and your real
 * stakeholder register appears in a labelled panel with an add form. The Orchestrator reads these
 * (groundable) and can prepare a new entry you confirm (draftable). The drag-to-confirm grid on real
 * data goes live later.
 */
function StakeholderLive({ onNavigate }: { onNavigate: (path: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const canWrite = me?.role === 'org_admin' || me?.role === 'super_admin';
  const stkQ = useQuery({ queryKey: ['stakeholders'], queryFn: () => api<Stakeholder[]>('/stakeholders') });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/master-data/categories') });
  const createM = useMutation({
    mutationFn: (input: Record<string, unknown>) => api<Stakeholder>('/stakeholders', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stakeholders'] }),
  });
  const removeM = useMutation({
    mutationFn: (id: string) => api<void>(`/stakeholders/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stakeholders'] }),
  });
  const seedM = useMutation({
    mutationFn: () => api<{ seeded: number }>('/demo/seed', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stakeholders'] }),
  });
  const wipeM = useMutation({
    mutationFn: () => api<{ wiped: number }>('/demo/wipe', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stakeholders'] }),
  });
  return (
    <StakeholderMap
      onNavigate={onNavigate}
      stakeholders={{
        rows: (stkQ.data ?? []).map((s) => ({ id: s.id, name: s.name, role: s.role, quadrant: s.quadrant, influence: s.influence, interest: s.interest, categoryId: s.categoryId, isDemo: s.isDemo })),
        categories: (catQ.data ?? []).map((c) => ({ id: c.id, name: c.name })),
        loading: stkQ.isLoading,
        error: stkQ.error ? (stkQ.error instanceof ApiError ? stkQ.error.message : 'Could not load stakeholders') : null,
        canWrite,
        create: async (input) => { await createM.mutateAsync(input as unknown as Record<string, unknown>); },
        remove: async (id) => { await removeM.mutateAsync(id); },
        seedDemo: canWrite ? async () => { await seedM.mutateAsync(); } : undefined,
        wipeDemo: canWrite ? async () => { await wipeM.mutateAsync(); } : undefined,
      }}
    />
  );
}

/**
 * Savings Pipeline, live (BP-03/D-026/D-033/D-115): the demo IL chart/table stay, and your real
 * initiatives (referencing the live suppliers + categories) appear in a labelled panel with an add
 * form. The IL-stacked chart + gate workflow go live as the multi-value-line model is persisted.
 */
function SavingsLive(): JSX.Element {
  const qc = useQueryClient();
  const me = qc.getQueryData<Me>(['me']);
  const canWrite = me?.role === 'org_admin' || me?.role === 'super_admin';
  const initQ = useQuery({ queryKey: ['savings'], queryFn: () => api<SavingsInitiative[]>('/savings/initiatives') });
  const supQ = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/master-data/suppliers') });
  const catQ = useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/master-data/categories') });
  const createM = useMutation({
    mutationFn: (input: Record<string, unknown>) => api<SavingsInitiative>('/savings/initiatives', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['savings'] }),
  });
  const removeM = useMutation({
    mutationFn: (id: string) => api<void>(`/savings/initiatives/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['savings'] }),
  });
  return (
    <SavingsPipeline
      savings={{
        rows: (initQ.data ?? []).map((i) => ({ id: i.id, supplierId: i.supplierId, categoryId: i.categoryId, title: i.title, bucket: i.bucket, ilStage: i.ilStage, value: i.value, currency: i.currency, targetYear: i.targetYear, isDemo: i.isDemo })),
        suppliers: (supQ.data ?? []).map((s) => ({ id: s.id, name: s.name })),
        categories: (catQ.data ?? []).map((c) => ({ id: c.id, name: c.name })),
        loading: initQ.isLoading,
        error: initQ.error ? (initQ.error instanceof ApiError ? initQ.error.message : 'Could not load savings initiatives') : null,
        canWrite,
        create: async (input) => { await createM.mutateAsync(input as unknown as Record<string, unknown>); },
        remove: async (id) => { await removeM.mutateAsync(id); },
      }}
    />
  );
}

/**
 * Settings → Members, live (D-151 Batch 4). List, invite and role changes over the existing audited
 * endpoints; the server enforces org_admin writes and refuses to demote the last admin.
 */
function MembersLive({ meEmail, canWrite, onBack }: { meEmail: string; canWrite: boolean; onBack: () => void }): JSX.Element {
  const qc = useQueryClient();
  const membersQ = useQuery({ queryKey: ['members'], queryFn: () => api<Member[]>('/members') });
  const refresh = (): Promise<void> => qc.invalidateQueries({ queryKey: ['members'] });
  const adapter: MembersAdapter = {
    members: (membersQ.data ?? []).map((m) => ({ userId: m.userId, email: m.email, role: m.role, status: m.status, createdAt: m.createdAt })),
    meEmail,
    loading: membersQ.isLoading,
    error: membersQ.error ? (membersQ.error instanceof ApiError ? membersQ.error.message : 'Could not load members') : null,
    canWrite,
    invite: async (email, role) => { await api<Member>('/members', { method: 'POST', body: JSON.stringify({ email, role }) }); await refresh(); },
    changeRole: async (userId, role) => { await api<Member>(`/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) }); await refresh(); },
  };
  return <SettingsMembers a={adapter} onBack={onBack} />;
}

/**
 * Settings → Organisation, live (D-151 Batch 4; closes U1). Name + the legal profile whose values the
 * contract-template render fills into {{CustomerAddress}}/{{GoverningLaw}}/{{Jurisdiction}}.
 */
function OrganisationLive({ canWrite, onNavigate, onBack }: { canWrite: boolean; onNavigate: (path: string) => void; onBack: () => void }): JSX.Element {
  const qc = useQueryClient();
  const orgQ = useQuery({ queryKey: ['org-current'], queryFn: () => api<Org>('/orgs/current') });
  const adapter: OrganisationAdapter = {
    org: orgQ.data ? { name: orgQ.data.name, slug: orgQ.data.slug, createdAt: orgQ.data.createdAt, legalAddress: orgQ.data.legalAddress, governingLaw: orgQ.data.governingLaw, jurisdiction: orgQ.data.jurisdiction } : null,
    loading: orgQ.isLoading,
    error: orgQ.error ? (orgQ.error instanceof ApiError ? orgQ.error.message : 'Could not load the organisation') : null,
    canWrite,
    save: async (patch) => {
      await api<Org>('/orgs/current', { method: 'PATCH', body: JSON.stringify(patch) });
      await qc.invalidateQueries({ queryKey: ['org-current'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
    },
  };
  return <SettingsOrganisation a={adapter} onNavigate={onNavigate} onBack={onBack} />;
}

/**
 * Settings → Audit Log, live (D-151 Batch 4). Read + CSV export over the existing org_admin-gated
 * endpoints; actor ids map to member emails where known. The export downloads via the same
 * cookie-authenticated fetch and preserves the server's filename.
 */
function AuditLive({ canRead, onBack }: { canRead: boolean; onBack: () => void }): JSX.Element {
  const membersQ = useQuery({ queryKey: ['members'], queryFn: () => api<Member[]>('/members'), enabled: canRead });
  const params = (f: AuditFilters): string => {
    const p = new URLSearchParams();
    if (f.action !== undefined) p.set('action', f.action);
    if (f.objectType !== undefined) p.set('objectType', f.objectType);
    p.set('limit', String(f.limit));
    return p.toString();
  };
  const adapter: AuditAdapter = {
    canRead,
    load: async (f) => {
      const events = await api<AuditEvent[]>(`/audit-events?${params(f)}`);
      return events.map((e): AuditEventView => ({ id: e.id, actorUserId: e.actorUserId, impersonatorUserId: e.impersonatorUserId, role: e.role, action: e.action, objectType: e.objectType, objectId: e.objectId, ip: e.ip, createdAt: e.createdAt }));
    },
    exportCsv: async (f) => {
      const res = await fetch(`/api/v1/audit-events/export?${params(f)}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const name = res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'audit-export.csv';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      URL.revokeObjectURL(url);
    },
    actorLabel: (userId) => {
      if (userId === null) return 'system';
      return (membersQ.data ?? []).find((m) => m.userId === userId)?.email ?? `${userId.slice(0, 8)}…`;
    },
  };
  return <AuditLog a={adapter} onBack={onBack} />;
}

/**
 * "Your accepted terms" — the acceptance archive (BP-22, D-145; Settings section). Reads the user's
 * own acceptance history and the stored document texts. The two published policies appear with
 * version 'current', resolved through the public current-version endpoint (same store).
 */
function AcceptedTermsLive({ userEmail, onBack }: { userEmail: string; onBack: () => void }): JSX.Element {
  const accQ = useQuery({ queryKey: ['terms-acceptances'], queryFn: getTermsAcceptances });
  const adapter: AcceptedTermsAdapter = {
    acceptances: accQ.data ?? null,
    loading: accQ.isLoading,
    error: accQ.error ? (accQ.error instanceof ApiError ? accQ.error.message : 'Could not load your acceptance history') : null,
    loadDocument: (docType, version) =>
      version === 'current' ? getPublicLegalDocument(docType) : getLegalDocument(docType, version),
    userEmail,
  };
  return <AcceptedTerms adapter={adapter} onBack={onBack} />;
}
